import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Products from '@/pages/Products';
import ProductFormFinal from '@/pages/ProductFormFinal';
import Categories from '@/pages/CategoriesEnhanced';
import Settings from '@/pages/Settings';
import CatalogGenerator from '@/pages/CatalogGenerator';
import NavigationBuilder from '@/pages/NavigationBuilder';
import OAuthCallback from '@/pages/OAuthCallback';
import SSODebug from '@/pages/SSODebug';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
}

function App() {
  const [user, setUser] = useState(null);
  const [isLoadingSSO, setIsLoadingSSO] = useState(false);
  const [ghlConnectionStatus, setGhlConnectionStatus] = useState(null);
  const [showGhlBanner, setShowGhlBanner] = useState(false);

  // Check GHL connection status after user is loaded
  useEffect(() => {
    if (user) {
      checkGHLConnection();
    }
  }, [user]);

  // Listen for connection success event from Settings page
  useEffect(() => {
    const handleConnectionSuccess = () => {
      // Re-check connection status and hide banner
      checkGHLConnection();
      setShowGhlBanner(false);
    };

    window.addEventListener('ghl-connection-success', handleConnectionSuccess);
    
    return () => {
      window.removeEventListener('ghl-connection-success', handleConnectionSuccess);
    };
  }, []);

  const checkGHLConnection = async () => {
    try {
      const response = await axiosInstance.get('/auth/check-ghl-connection');
      setGhlConnectionStatus(response.data);
      
      // Show banner if connection needs attention (including not connected at all)
      if (!response.data.connected) {
        setShowGhlBanner(true);
      }
    } catch (error) {
      console.error('Failed to check GHL connection:', error);
    }
  };

  useEffect(() => {
    // Check if we're in an iframe (GoHighLevel Custom Page)
    const isInIframe = window.self !== window.top;
    
    console.log('[SSO] App loaded. In iframe:', isInIframe);
    console.log('[SSO] Current URL:', window.location.href);
    console.log('[SSO] Referrer:', document.referrer);
    
    if (isInIframe) {
      console.log('[SSO] Detected iframe environment - initiating SSO authentication');
      // We're inside GHL - try SSO authentication
      handleGHLSSO();
    } else {
      console.log('[SSO] Not in iframe - using regular login flow');
      // Regular login - check stored user
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
  }, []);

  const handleGHLSSO = async () => {
    // Check if already authenticated
    const token = localStorage.getItem('token');
    if (token) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        return;
      }
    }

    setIsLoadingSSO(true);
    console.log('[SSO] Starting GoHighLevel SSO authentication...');
    
    try {
      // Request encrypted user data from GHL parent window
      const encryptedUserData = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error('[SSO] Timeout: No response from GHL parent window after 15 seconds');
          reject(new Error('SSO request timeout - GoHighLevel did not respond. Make sure your app is properly configured as a Custom Page in your GHL marketplace app settings.'));
        }, 15000); // Increased timeout to 15 seconds

        console.log('[SSO] Sending REQUEST_USER_DATA to parent window...');
        
        // Request user data from parent window
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');

        // Listen for the response
        const messageHandler = ({ data, origin }) => {
          console.log('[SSO] Received message from parent:', data, 'Origin:', origin);
          
          if (data.message === 'REQUEST_USER_DATA_RESPONSE') {
            console.log('[SSO] Got REQUEST_USER_DATA_RESPONSE');
            clearTimeout(timeout);
            window.removeEventListener('message', messageHandler);
            resolve(data.payload);
          }
        };

        window.addEventListener('message', messageHandler);
      });

      console.log('[SSO] Received encrypted SSO data from GHL. Length:', encryptedUserData?.length);

      // Send encrypted data to backend for decryption
      const response = await axios.post(`${API}/auth/decrypt-sso`, {
        encryptedData: encryptedUserData
      });

      const { token: appToken, user: userData } = response.data;

      // Store token and user data
      localStorage.setItem('token', appToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      console.log('[SSO] ✅ SSO authentication successful!', userData);
      toast.success(`Welcome back, ${userData.name || userData.email}!`);
    } catch (error) {
      console.error('[SSO] ❌ GHL SSO authentication failed:', error);
      console.error('[SSO] Error details:', {
        message: error.message,
        response: error.response?.data,
        stack: error.stack
      });
      
      // Show a helpful error message
      if (error.message.includes('timeout')) {
        toast.error('SSO timeout: GoHighLevel did not respond. Please check your app configuration or try logging in manually.', {
          duration: 6000
        });
      } else {
        toast.error(`SSO failed: ${error.response?.data?.detail || error.message}. Please try logging in manually.`, {
          duration: 6000
        });
      }
    } finally {
      setIsLoadingSSO(false);
    }
  };

  if (isLoadingSSO) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-700 font-medium mb-2">Authenticating with GoHighLevel...</p>
          <p className="text-sm text-slate-500">Requesting user data from parent window</p>
          <p className="text-xs text-slate-400 mt-4">If this takes too long, please check browser console (F12) for details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        {/* GHL Connection Status Banner */}
        {showGhlBanner && ghlConnectionStatus && !ghlConnectionStatus.connected && (
          <div className={`fixed top-0 left-0 right-0 z-50 text-white px-4 py-3 shadow-lg ${
            ghlConnectionStatus.status === 'not_connected' 
              ? 'bg-blue-500' 
              : 'bg-yellow-500'
          }`}>
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                {ghlConnectionStatus.status === 'not_connected' ? (
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">
                    {ghlConnectionStatus.message}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a 
                  href={ghlConnectionStatus.status === 'not_connected' ? '/settings' : '/settings?reconnect=true'}
                  className={`px-4 py-1 rounded font-medium text-sm transition-colors whitespace-nowrap ${
                    ghlConnectionStatus.status === 'not_connected'
                      ? 'bg-white text-blue-600 hover:bg-blue-50'
                      : 'bg-white text-yellow-600 hover:bg-yellow-50'
                  }`}
                >
                  {ghlConnectionStatus.status === 'not_connected' ? 'Connect Now' : 'Reconnect Now'}
                </a>
                <button 
                  onClick={() => setShowGhlBanner(false)}
                  className="text-white hover:text-yellow-100 transition-colors"
                  aria-label="Close banner"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        
        <Routes>
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/sso-debug" element={<SSODebug />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout user={user}>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <Layout user={user}>
                  <Products user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/new"
            element={
              <ProtectedRoute>
                <Layout user={user}>
                  <ProductFormFinal user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/edit/:id"
            element={
              <ProtectedRoute>
                <Layout user={user}>
                  <ProductFormFinal user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <Layout user={user}>
                  <Categories user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalog"
            element={
              <ProtectedRoute>
                <Layout user={user}>
                  <CatalogGenerator />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/navigation"
            element={
              <ProtectedRoute>
                <Layout user={user}>
                  <NavigationBuilder />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout user={user}>
                  <Settings user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
